import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "calculos-history-v1";
const CHANGE_EVENT = "calculos-history:change";
const PREFILL_EVENT = "calculos-history:prefill";

export type CalculoType =
  | "revisionais"
  | "atualizacao"
  | "trabalhistas"
  | "fgts"
  | "pensao"
  | "aluguel"
  | "superendividamento"
  | "pasep"
  | "dosimetria"
  | "progressao"
  | "rmc"
  | "inss"
  | "divorcio";

export interface HistoryEntry {
  id: string;
  type: CalculoType;
  label: string;
  values: Record<string, any>;
  summary: string;
  createdAt: number;
}

const readAll = (): HistoryEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeAll = (entries: HistoryEntry[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch (e) {
    console.error("Erro ao salvar histórico de cálculos", e);
  }
};

export const addToHistory = (
  entry: Omit<HistoryEntry, "id" | "createdAt">
): HistoryEntry => {
  const full: HistoryEntry = {
    ...entry,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: Date.now(),
  };
  const list = readAll();
  list.unshift(full);
  writeAll(list.slice(0, 100));
  return full;
};

export const deleteFromHistory = (id: string) => {
  writeAll(readAll().filter((e) => e.id !== id));
};

export const clearHistory = () => writeAll([]);

export const requestPrefill = (type: CalculoType, values: Record<string, any>) => {
  window.dispatchEvent(
    new CustomEvent(PREFILL_EVENT, { detail: { type, values } })
  );
};

export const useCalculoHistoryList = () => {
  const [items, setItems] = useState<HistoryEntry[]>(() => readAll());
  useEffect(() => {
    const refresh = () => setItems(readAll());
    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);
  return items;
};

/**
 * Escuta pedidos de prefill para um tipo específico de cálculo.
 */
export const useCalculoPrefill = (
  type: CalculoType,
  apply: (values: Record<string, any>) => void
) => {
  const applyRef = useCallback(apply, [apply]);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.type === type && detail.values) {
        applyRef(detail.values);
      }
    };
    window.addEventListener(PREFILL_EVENT, handler);
    return () => window.removeEventListener(PREFILL_EVENT, handler);
  }, [type, applyRef]);
};
